import { useEffect, useState } from "react";
import { server } from "../../server";
import axios from "axios";
import { Link } from "react-router-dom";
import { classList, sectionList } from "../../utils";
import { toast } from "react-toastify";
import DownloadAdmitCard from "../../components/DownloadAdmitCard";

function AdmitCard() {
  const [complainBoxDetails, setComplainBoxDetails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const recordsPerPage = 10;
  const [selectedClass, setSelectedClass] = useState("Uday");
  const [selectedSection, setSelectedSection] = useState("B");
  const [isDownloading, setIsDownloading] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState("All");

  const [selectedStream, setSelectedStream] = useState("");

  const STREAM_OPTIONS = ["Science", "Commerce", "Humanities"];
  const STREAM_REQUIRED_CLASSES = ["XI", "XII"];

  const monthList = [
    "All",
    "april",
    "may",
    "june",
    "july",
    "august",
    "september",
    "october",
    "november",
    "december",
    "january",
    "february",
    "march",
  ];

  // Sets the class and clears any previously selected stream when the
  // new class doesn't require one (so a stale stream never gets sent).
  const handleSelectClass = (cls) => {
    setSelectedClass(cls);
    if (!STREAM_REQUIRED_CLASSES.includes(cls)) {
      setSelectedStream("");
    }
  };

  const handleDownloadClassWiseAdmitCard = async () => {
    if (!selectedClass) {
      toast.error("Select Class", { position: "top-right" });
      return;
    }
    if (STREAM_REQUIRED_CLASSES.includes(selectedClass) && !selectedStream) {
      toast.error("Select Stream", { position: "top-right" });
      return;
    }

    try {
      setIsDownloading(true);
      let url = `${server}/GetAdmitCardByClassAndSection?class=${selectedClass}`;
      if (selectedMonth !== "All") {
        url += `&month=${selectedMonth}`;
      }
      if (STREAM_REQUIRED_CLASSES.includes(selectedClass) && selectedStream) {
        url += `&stream=${selectedStream}`;
      }

      const response = await fetch(url);
      const data = await response.json();

      const students = data?.allStudents || [];
      const admitCardDetail = data?.admitCardDetail?.[0] || null;

      if (students.length === 0) {
        toast.error("No students found", { position: "top-right" });
        return;
      }

      const sortedStudents = students.sort(
        (a, b) =>
          Number(a.studentDetails.rollNo) - Number(b.studentDetails.rollNo),
      );

      // NEW: resolve teacher for each unique section present in the results
      const uniqueSections = Array.from(
        new Set(sortedStudents.map((s) => s.studentDetails.currentSection)),
      );

      const teacherMap = {};
      const token = localStorage.getItem("token");

      await Promise.all(
        uniqueSections.map(async (section) => {
          const key = `${selectedClass}|||${section}`;
          try {
            const res = await axios.post(
              `${server}/GetUserDetailByClassTeacherAndSection`,
              { classTeacher: selectedClass, section },
              {
                headers: {
                  Authorization: token,
                  "Content-Type": "application/json",
                },
              },
            );
            teacherMap[key] = res?.data?.data || null;
          } catch (err) {
            teacherMap[key] = null;
          }
        }),
      );

      await DownloadAdmitCard(sortedStudents, admitCardDetail, teacherMap);

      const closeButton = document.querySelector('[data-bs-dismiss="modal"]');
      if (closeButton) closeButton.click();

      setSelectedClass("");
      setSelectedStream("");
    } catch (error) {
      toast.error("Something went wrong", { position: "top-right" });
      console.error(error);
    } finally {
      setIsDownloading(false);
    }
  };

  const handleDownloadClassSectionWiseAdmitCard = async () => {
    if (!selectedClass || !selectedSection) {
      toast.error("Select Class & Section", { position: "top-right" });
      return;
    }
    if (STREAM_REQUIRED_CLASSES.includes(selectedClass) && !selectedStream) {
      toast.error("Select Stream", { position: "top-right" });
      return;
    }

    try {
      setIsDownloading(true);

      let url = `${server}/GetAdmitCardByClassAndSection?class=${selectedClass}&section=${selectedSection}`;
      if (selectedMonth !== "All") {
        url += `&month=${selectedMonth}`;
      }
      if (STREAM_REQUIRED_CLASSES.includes(selectedClass) && selectedStream) {
        url += `&stream=${selectedStream}`;
      }
      const response = await fetch(url);
      const data = await response.json();

      const students = data?.allStudents || [];
      const admitCardDetail = data?.admitCardDetail?.[0] || null;

      if (students.length === 0) {
        toast.error("No students found", { position: "top-right" });
        return;
      }

      const sortedStudents = students.sort(
        (a, b) =>
          Number(a.studentDetails.rollNo) - Number(b.studentDetails.rollNo),
      );

      // NEW: fetch the class teacher once, since class & section are known
      let teacherData = null;
      try {
        const teacherRes = await axios.post(
          `${server}/GetUserDetailByClassTeacherAndSection`,
          { classTeacher: selectedClass, section: selectedSection },
          {
            headers: {
              Authorization: localStorage.getItem("token"),
              "Content-Type": "application/json",
            },
          },
        );
        teacherData = teacherRes?.data?.data || null;
      } catch (err) {
        console.error("Failed to fetch class teacher", err);
      }

      await DownloadAdmitCard(sortedStudents, admitCardDetail, teacherData);

      const closeButton = document.querySelector('[data-bs-dismiss="modal"]');
      if (closeButton) closeButton.click();

      setSelectedClass("");
      setSelectedSection("");
      setSelectedStream("");
    } catch (error) {
      toast.error("Something went wrong", { position: "top-right" });
      console.error(error);
    } finally {
      setIsDownloading(false);
    }
  };

  const fetchAdmitCardDetails = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${server}/GetAllAdmitCards`, {
        headers: { Authorization: `${localStorage.getItem("token")}` },
      });
      const responseData = response.data;

      if (Array.isArray(responseData)) {
        setComplainBoxDetails(responseData);
      } else {
        console.error("Invalid data format from API", response.data);
      }
    } catch (error) {
      console.error("Error fetching fee details:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAdmitCardDetails();
  }, []);

  // Filter
  const filteredRecords = complainBoxDetails.filter((ComplainBox) => {
    const searchLower = searchTerm.toLowerCase();

    return Object.values(ComplainBox || {}).some(
      (value) =>
        typeof value === "string" && value.toLowerCase().includes(searchLower),
    );
  });

  // Calculate pagination
  const indexOfLastRecord = currentPage * recordsPerPage;
  const indexOfFirstRecord = indexOfLastRecord - recordsPerPage;
  const currentRecords = filteredRecords.slice(
    indexOfFirstRecord,
    indexOfLastRecord,
  );
  const totalPages = Math.ceil(filteredRecords.length / recordsPerPage);

  // Page Navigation
  const nextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const prevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  return (
    <>
      <div id="db-wrapper">
        <main id="page-content">
          <div class="container-fluid my-3 px-3">
            <div className="row g-2">
              <div className="col-lg-8">
                <div class="table-responsive border-0 overflow-y-hidden bg-white rounded-4">
                  <table class="table mb-0 text-nowrap table-centered">
                    <thead class="table-light">
                      <tr>
                        <th className="w-10">No.</th>
                        <th className="lh-1 w-60">
                          Details
                          <i className="bi bi-info-circle ms-2 text-dark"></i>
                          <br />
                          <span className="fs-8 fw-normal text-secondary">
                            Displaying Information as per Record
                          </span>
                        </th>
                        <th className="lh-1 w-30">
                          Action
                          <i className="bi bi-person-gear ms-2 text-dark"></i>
                          <br />
                          <span className="fs-8 fw-normal text-secondary">
                            Role-Specific Action Panel
                          </span>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {currentRecords.map((complain, index) => (
                        <tr key={index}>
                          <td>{indexOfFirstRecord + index + 1}.</td>
                          <td className="lh-sm py-4 text-wrap">
                            <span class="text-dark">Session</span> &mdash;{" "}
                            {complain.session}
                            <br />
                            <span class="text-dark">Term</span> &mdash;{" "}
                            {complain.term}
                            <br />
                            <span class="text-dark">Class</span> &mdash;{" "}
                            {complain.class}
                            <br />
                            <span class="text-dark">Section</span> &mdash;{" "}
                            {complain.section.join(",")}
                            <br />
                            <span class="text-dark">Date</span> &mdash;{" "}
                            <span className="text-uppercase">
                              {new Date(complain.createdAt).toLocaleString(
                                "en-GB",
                                {
                                  day: "2-digit",
                                  month: "2-digit",
                                  year: "numeric",
                                  hour: "2-digit",
                                  minute: "2-digit",
                                  hour12: true,
                                },
                              )}
                            </span>
                          </td>
                          <td>
                            <div className="d-flex flex-column gap-1 width-fit-content">
                              <span className="badge bg-success-soft cursor-pointer px-4 rounded-3 btn-py-sm">
                                Update
                              </span>
                              <span class="badge bg-danger-soft cursor-pointer px-4 rounded-3 btn-py-sm">
                                Delete
                              </span>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="col-lg-4">
                <div class="">
                  <div className="mb-3">
                    <div className="d-flex bg-white justify-content-between rounded-4">
                      <form
                        className="d-flex align-items-center"
                        onSubmit={(e) => e.preventDefault()}
                      >
                        <span className="position-absolute ps-3 search-icon">
                          <i className="bi bi-filter-circle-fill ms-1" />
                        </span>
                        <input
                          type="search"
                          className="form-control ps-7 rounded-3 border-0 rounded-4"
                          placeholder="Filter Data"
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                        />
                      </form>
                      <Link
                        to="/add-examination-details"
                        class="btn btn-warning px-5 fs-6 fw-medium rounded-4 fit-content"
                      >
                        Add Details
                      </Link>
                    </div>
                    <div className="mt-2 ms-4">
                      <nav aria-label="breadcrumb">
                        <ol class="breadcrumb pe-3">
                          <li class="breadcrumb-item">
                            <a href="#">Examinations</a>
                          </li>
                          <li
                            class="breadcrumb-item active"
                            aria-current="page"
                          >
                            Admit Card
                          </li>
                        </ol>
                      </nav>
                    </div>
                  </div>

                  <div className="p-4 bg-white rounded-4">
                    <div className="d-flex justify-content-between align-items-center">
                      <div className="d-flex align-items-center">
                        {loading ? (
                          <div
                            className="spinner-border text-dark data-loader-spinner"
                            role="status"
                          ></div>
                        ) : (
                          <img src="/assets/images/folder.svg" />
                        )}

                        <p className="my-0 fw-semibold lh-1 fs-5 text-dark mx-3">
                          Admit Card
                          <br />
                          <span className="text-secondary fs-6 fw-medium lh-sm">
                            0.2 MB Size
                          </span>
                        </p>
                      </div>
                      <span className="badge bg-pink rounded-3 px-4 py-2 me-1 mt-1 cursor-pointer">
                        Export
                      </span>
                    </div>

                    <div class="px-4 py-2 mt-3 bg-light rounded-4">
                      <li className="list-group-item px-0">
                        <a
                          className="d-flex align-items-center text-inherit mb-0 justify-content-between"
                          data-bs-toggle="collapse"
                          href="#specifications"
                          role="button"
                          aria-expanded="false"
                          aria-controls="specifications"
                        >
                          <p className="m-0 fw-medium lh-sm text-dark">
                            Download Report
                          </p>
                          <span className="chevron-arrow ms-4">
                            <i className="bi bi-chevron-down fs-4" />
                          </span>
                        </a>
                        <div
                          className="collapse"
                          id="specifications"
                          data-bs-parent="#ecommerceAccordion"
                        >
                          <div className="py-3">
                            <div class="rounded-4 d-flex flex-wrap">
                              <span class="btn btn-outline-primary btn-sm px-4 rounded-3 mb-1 me-1">
                                All Examination Report
                              </span>
                              <span class="btn btn-outline-primary btn-sm px-4 rounded-3 mb-1 me-1">
                                Term Wise
                              </span>
                              <span class="btn btn-outline-primary btn-sm px-4 rounded-3 mb-1 me-1">
                                Examination Class Wise Report
                              </span>
                            </div>
                          </div>
                        </div>
                      </li>
                    </div>

                    <div class="mt-1">
                      <div className="row g-1">
                        <div className="col-12">
                          <div
                            data-bs-toggle="modal"
                            data-bs-target="#admitCardClassSectionWiseModal"
                            className="p-3 bg-success-soft rounded-4 cursor-pointer"
                          >
                            <p className="m-0 text-center fw-medium lh-sm text-dark">
                              Download Admit Card (PDF)
                            </p>
                            <p className="text-center fs-5 m-0 lh-1">
                              Class / Section Wise
                            </p>
                          </div>
                        </div>
                        <div className="col-12">
                          <div
                            data-bs-toggle="modal"
                            data-bs-target="#admitCardClassWiseModal"
                            className="p-3 bg-info-soft rounded-4 cursor-pointer"
                          >
                            <p className="m-0 text-center fw-medium lh-sm text-dark">
                              Download Admit Card (PDF)
                            </p>
                            <p className="text-center fs-5 m-0 lh-1">
                              Class Wise
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="my-4">
                    <nav>
                      <ul className="pagination justify-content-center mb-0">
                        <li
                          className={`page-item ${
                            currentPage === 1 ? "disabled" : ""
                          }`}
                        >
                          <button className="page-link" onClick={prevPage}>
                            <i className="bi bi-chevron-left"></i>
                          </button>
                        </li>

                        {(() => {
                          const pageNumbers = [];
                          pageNumbers.push(1);

                          if (currentPage > 3) {
                            pageNumbers.push("left-ellipsis");
                          }

                          if (currentPage !== 1 && currentPage !== totalPages) {
                            pageNumbers.push(currentPage);
                          }

                          if (currentPage < totalPages - 2) {
                            pageNumbers.push("right-ellipsis");
                          }

                          if (totalPages > 1) {
                            pageNumbers.push(totalPages);
                          }

                          return pageNumbers.map((page, index) => {
                            if (
                              page === "left-ellipsis" ||
                              page === "right-ellipsis"
                            ) {
                              return (
                                <li key={index} className="page-item disabled">
                                  <span className="page-link">...</span>
                                </li>
                              );
                            }

                            return (
                              <li
                                key={index}
                                className={`page-item ${
                                  currentPage === page ? "active" : ""
                                }`}
                              >
                                <button
                                  className="page-link"
                                  onClick={() => setCurrentPage(page)}
                                >
                                  {page}
                                </button>
                              </li>
                            );
                          });
                        })()}

                        <li
                          className={`page-item ${
                            currentPage === totalPages ? "disabled" : ""
                          }`}
                        >
                          <button className="page-link" onClick={nextPage}>
                            <i className="bi bi-chevron-right"></i>
                          </button>
                        </li>
                      </ul>
                    </nav>
                  </div>
                </div>

                <div
                  class="modal m-0 fade"
                  id="admitCardClassSectionWiseModal"
                  tabindex="-1"
                  aria-labelledby="admitCardClassSectionWiseModalLabel"
                  aria-hidden="true"
                >
                  <div class="modal-dialog modal-dialog-centered">
                    <div class="modal-content rounded-5 overflow-hidden">
                      <div class="p-4 bg-danger-soft d-flex justify-content-end">
                        <i
                          className="bi bi-x-lg text-dark fs-3 cursor-pointer position-relative z-1"
                          data-bs-dismiss="modal"
                          aria-label="Close"
                        ></i>
                      </div>
                      <div class="pt-0 p-5">
                        <div className="d-flex justify-content-center mt-n8">
                          <img
                            src="/assets/images/report.jpg"
                            width="90"
                            className="object-fit-contain p-3 rounded-5 bg-white"
                          />
                        </div>

                        <div className="mt-2">
                          <h5 class="text-center lh-1 fw-medium">
                            Class / Section Wise Admit Card <br />
                            <span className="text-secondary">
                              Generate Admit Card
                            </span>
                          </h5>
                        </div>

                        <div className="mt-4">
                          <div className="bg-light rounded-pill py-2 mb-3">
                            <h5 class="fs-5 text-center m-0 lh-1 fw-medium">
                              Select Class
                            </h5>
                          </div>

                          <div className="d-flex flex-wrap justify-content-center gap-1 px-4">
                            {classList.map((cls, index) => (
                              <div
                                key={index}
                                className={`bg-dark-soft rounded-pill cursor-pointer px-3 py-1 fs-6 ${
                                  selectedClass === cls
                                    ? "bg-warning text-white border-warning"
                                    : ""
                                }`}
                                onClick={() => handleSelectClass(cls)}
                              >
                                {cls}
                              </div>
                            ))}
                          </div>
                        </div>

                        {STREAM_REQUIRED_CLASSES.includes(selectedClass) && (
                          <div className="mt-4">
                            <div className="bg-light rounded-pill py-2 mb-3">
                              <h5 class="fs-5 text-center m-0 lh-1 fw-medium">
                                Select Stream
                              </h5>
                            </div>

                            <div className="d-flex flex-wrap justify-content-center gap-1 px-4">
                              {STREAM_OPTIONS.map((stream, index) => (
                                <div
                                  key={index}
                                  className={`bg-dark-soft rounded-pill cursor-pointer px-3 py-1 fs-6 ${
                                    selectedStream === stream
                                      ? "bg-warning text-white border-warning"
                                      : ""
                                  }`}
                                  onClick={() => setSelectedStream(stream)}
                                >
                                  {stream}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        <div className="mt-4">
                          <div className="bg-light rounded-pill py-2 mb-3">
                            <h5 class="fs-5 text-center m-0 lh-1 fw-medium">
                              Select Section
                            </h5>
                          </div>

                          <div className="d-flex flex-wrap justify-content-center gap-1 px-lg-8 px-5 mx-lg-4">
                            {sectionList.map((sec, index) => (
                              <div
                                key={index}
                                className={`bg-dark-soft rounded-pill cursor-pointer px-2 py-1 fs-6 ${
                                  selectedSection === sec
                                    ? "bg-warning text-white border-warning"
                                    : ""
                                }`}
                                onClick={() => setSelectedSection(sec)}
                              >
                                <span className="mx-1">{sec}</span>
                              </div>
                            ))}
                          </div>
                        </div>

                        <div className="mt-4">
                          <div className="bg-light rounded-pill py-2 mb-3">
                            <h5 class="fs-5 text-center m-0 lh-1 fw-medium">
                              Select Month &nbsp;(Student's Fee Paid Upto)
                            </h5>
                          </div>

                          <div className="d-flex flex-wrap justify-content-center gap-1 px-1">
                            {monthList.map((month, index) => (
                              <div
                                key={index}
                                className={`bg-dark-soft rounded-pill cursor-pointer px-3 py-1 fs-6 text-capitalize ${
                                  selectedMonth === month
                                    ? "bg-warning text-white border-warning"
                                    : ""
                                }`}
                                onClick={() => setSelectedMonth(month)}
                              >
                                {month}
                              </div>
                            ))}
                          </div>
                        </div>

                        <div className="mt-5 d-flex justify-content-center flex-column align-items-center">
                          <div>
                            {isDownloading ? (
                              <div class="bg-primary md-circle-button fw-medium">
                                <div
                                  class="spinner-border spinner-border-md text-white"
                                  role="status"
                                ></div>
                              </div>
                            ) : (
                              <button
                                onClick={
                                  handleDownloadClassSectionWiseAdmitCard
                                }
                                class="btn btn-primary rounded-pill px-6 fs-5 fw-medium"
                              >
                                Download Admit Card
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div
                  class="modal m-0 fade"
                  id="admitCardClassWiseModal"
                  tabindex="-1"
                  aria-labelledby="admitCardClassWiseLabel"
                  aria-hidden="true"
                >
                  <div class="modal-dialog modal-dialog-centered">
                    <div class="modal-content rounded-5 overflow-hidden">
                      <div class="p-4 bg-danger-soft d-flex justify-content-end">
                        <i
                          className="bi bi-x-lg text-dark fs-3 cursor-pointer position-relative z-1"
                          data-bs-dismiss="modal"
                          aria-label="Close"
                        ></i>
                      </div>
                      <div class="pt-0 p-5">
                        <div className="d-flex justify-content-center mt-n8">
                          <img
                            src="/assets/images/report.jpg"
                            width="90"
                            className="object-fit-contain p-3 rounded-5 bg-white"
                          />
                        </div>

                        <div className="mt-2">
                          <h5 class="text-center lh-1 fw-medium">
                            Class Wise Admit Card <br />
                            <span className="text-secondary">
                              Generate Admit Card
                            </span>
                          </h5>
                        </div>

                        <div className="mt-4">
                          <div className="bg-light rounded-pill py-2 mb-3">
                            <h5 class="fs-5 text-center m-0 lh-1 fw-medium">
                              Select Class
                            </h5>
                          </div>

                          <div className="d-flex flex-wrap justify-content-center gap-1 px-1">
                            {classList.map((cls, index) => (
                              <div
                                key={index}
                                className={`bg-dark-soft rounded-pill cursor-pointer px-3 py-1 fs-6 ${
                                  selectedClass === cls
                                    ? "bg-warning text-white border-warning"
                                    : ""
                                }`}
                                onClick={() => handleSelectClass(cls)}
                              >
                                {cls}
                              </div>
                            ))}
                          </div>
                        </div>

                        {STREAM_REQUIRED_CLASSES.includes(selectedClass) && (
                          <div className="mt-4">
                            <div className="bg-light rounded-pill py-2 mb-3">
                              <h5 class="fs-5 text-center m-0 lh-1 fw-medium">
                                Select Stream
                              </h5>
                            </div>

                            <div className="d-flex flex-wrap justify-content-center gap-1 px-4">
                              {STREAM_OPTIONS.map((stream, index) => (
                                <div
                                  key={index}
                                  className={`bg-dark-soft rounded-pill cursor-pointer px-3 py-1 fs-6 ${
                                    selectedStream === stream
                                      ? "bg-warning text-white border-warning"
                                      : ""
                                  }`}
                                  onClick={() => setSelectedStream(stream)}
                                >
                                  {stream}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        <div className="mt-4">
                          <div className="bg-light rounded-pill py-2 mb-3">
                            <h5 class="fs-5 text-center m-0 lh-1 fw-medium">
                              Select Month &nbsp;(Student's Fee Paid Upto)
                            </h5>
                          </div>

                          <div className="d-flex flex-wrap justify-content-center gap-1 px-4">
                            {monthList.map((month, index) => (
                              <div
                                key={index}
                                className={`bg-dark-soft rounded-pill cursor-pointer px-3 py-1 fs-6 text-capitalize ${
                                  selectedMonth === month
                                    ? "bg-warning text-white border-warning"
                                    : ""
                                }`}
                                onClick={() => setSelectedMonth(month)}
                              >
                                {month}
                              </div>
                            ))}
                          </div>
                        </div>

                        <div className="mt-5 d-flex justify-content-center flex-column align-items-center">
                          <div>
                            {isDownloading ? (
                              <div class="bg-primary md-circle-button fw-medium">
                                <div
                                  class="spinner-border spinner-border-md text-white"
                                  role="status"
                                ></div>
                              </div>
                            ) : (
                              <button
                                onClick={handleDownloadClassWiseAdmitCard}
                                class="btn btn-primary rounded-pill px-6 fs-5 fw-medium"
                              >
                                Download Admit Card
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    </>
  );
}

export default AdmitCard;
